import type {
  Cb2PropertyTypeFor,
  DoodFieldType,
  FieldValueByType,
} from './descriptor';
import type { CasesValue, ParamRef, SerializableValue } from './bloc-definition';

/**
 * Context available to dashboard-side visibility and label predicates, plus
 * `propertyValue` `dashboardVisibilityWhen`.
 */
export type DashboardVisibilityContext = {
  entity: {
    plugins: readonly string[];
  };
  user: {
    roles: readonly string[];
  };
  devicePreview: 'mobile' | 'tablet' | 'desktop';
};

/**
 * Runtime context used to evaluate `BlockCondition` predicates on the webapp
 * side. Mirrors the dimensions handled by the legacy
 * `EvaluateBlockVisibilityService` so a declarative condition maps 1:1 to a
 * runtime check. Kept framework-agnostic; the webapp wires its stores into it.
 */
export type BlockContext = {
  locale?: string;
  device?: 'mobile' | 'desktop' | 'kiosk';
  userAuth?: 'authenticated' | 'anonymous';
  timeOfDay?: { now: string };
  weekday?: { now: Date };
  dateRange?: { now: Date };
  journeyParameters?: Record<string, unknown>;
  cartType?: string;
  shop?: Record<string, unknown>;
  userField?: Record<string, unknown>;
  isLoyaltyMember?: boolean;
  /** User tag labels from `/users/me` (e.g. Como `étudiant`). */
  userTags?: readonly string[];
  /** True when the storefront URL has `?test=true`. */
  isTestMode?: boolean;
  customExpert?: Record<string, unknown>;
};

/**
 * Declarative context condition selecting a conditional value. Replaces the
 * legacy `condition: boolean` and the JS resolver: the same predicate shape is
 * serialized to `$cases` in the compiled CB2 spec and evaluated at runtime by
 * the webapp (aligned with `EvaluateBlockVisibilityService`).
 */
export type BlockCondition =
  | { type: 'locale'; options: { in: readonly string[] } }
  | {
      type: 'device';
      options: { in: ReadonlyArray<'mobile' | 'desktop' | 'kiosk'> };
    }
  | {
      type: 'userAuth';
      options: { visibility: 'authenticated' | 'anonymous' };
    }
  | { type: 'timeOfDay'; options: { start: string; end: string } }
  | { type: 'weekday'; options: { selectedDays: readonly number[] } }
  | { type: 'dateRange'; options: { start: string; end: string } }
  | {
      type: 'journeyParameter';
      options: {
        paramKey: string;
        operator:
          | 'defined'
          | 'notDefined'
          | 'isTrue'
          | 'isFalse'
          | 'eq'
          | 'neq'
          | 'exists'
          | 'equals'
          | 'notEquals'
          | 'truthy'
          | 'falsy'
          | string;
        value?: unknown;
        paramType?: string;
      };
    }
  | {
      type:
        | 'cartType'
        | 'shop'
        | 'userField'
        | 'loyaltyMember'
        | 'customExpert';
      options: Record<string, unknown>;
    }
  | {
      type: 'userTag';
      options: { label: string; condition: 'has' | 'hasNot' };
    }
  | {
      type: 'testMode';
      options: { visibility: 'inTestMode' | 'notInTestMode' };
    };

/**
 * A single candidate value with the declarative condition that selects it. The
 * first candidate whose `condition` matches the runtime `BlockContext` wins.
 */
export type ConditionalPropertyValue<Value> = {
  condition: BlockCondition;
  value: Value;
  description?: string;
};

/**
 * Marker brands so a `PropertyValue` and a `DescriptorControlField` stay
 * distinguishable at the type level even when both carry the same field type.
 * A literal string brand (rather than `unique symbol`) keeps runtime casts
 * sound while still preventing accidental cross-assignment. These brands are
 * library-internal and never leak into the CB2 projection (see
 * `buildBlocDefinition`).
 */
export interface PropertyValueBrand {
  readonly __doodPropertyValue: typeof PROPERTY_VALUE_BRAND;
}

export interface DescriptorControlFieldBrand {
  readonly __doodDescriptorControlField: typeof DESCRIPTOR_CONTROL_FIELD_BRAND;
}

export const PROPERTY_VALUE_BRAND = '__doodPropertyValue' as const;
export const DESCRIPTOR_CONTROL_FIELD_BRAND = '__doodDescriptorControlField' as const;

/**
 * The literal value accepted by `propertyValue.value`. Either a typed static
 * value, a `$param` reference, or a `$cases` conditional — all serializable.
 */
export type PropertyValueInput<Type extends DoodFieldType> =
  | FieldValueByType<Type>
  | ParamRef
  | CasesValue;

export type PropertyValue<Type extends DoodFieldType> = {
  type: Type;
  kind: 'static' | 'conditional';
  /** Static value, when `kind === 'static'`. */
  value?: PropertyValueInput<Type>;
  /** Conditions, when `kind === 'conditional'`. */
  conditions?: readonly ConditionalPropertyValue<PropertyValueInput<Type>>[];
  description?: string;
  options?: Record<string, unknown>;
  /** Dashboard roles allowed to see the field, e.g. `['ADMIN']`. */
  allowedRoles?: readonly string[];
  dashboardVisibilityWhen?: PropertyValueDashboardVisibilityPredicate;
} & PropertyValueBrand;

export type DescriptorControlField<Type extends DoodFieldType> = {
  type: Type;
  value?: FieldValueByType<Type>;
  description?: string;
  options?: Record<string, unknown>;
  dashboardVisibilityWhen?: PropertyValueDashboardVisibilityPredicate;
} & DescriptorControlFieldBrand;

export type PropertyValueDashboardVisibilityPredicate = (
  context: DashboardVisibilityContext,
) => boolean;

/**
 * Input handed to `evaluateBlockCondition`. Exposed so the webapp resolver and
 * tests can share the same evaluation contract.
 */
export type BlockConditionResolveInput = {
  context: BlockContext;
};

/* -------------------------------------------------------------------------- */
/* paramRef helper                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Builds a serializable `$param` reference to a journey parameter, e.g.
 * `paramRef('brand.name')`. The legacy alias `$var` is accepted on input via
 * `paramRef(..., { alias: '$var' })` and normalized to `$param` in CB2 output.
 */
export function paramRef(key: string): ParamRef {
  return { $param: key };
}

/** Built-in journey parameter that owns the shared color palette. */
export const DEFAULT_COLOR_PALETTE_PARAM_KEY = 'ui.colors';

/**
 * Projects a legacy palette color token to its existing CB2 common value.
 * The legacy runtime color object is intentionally left untouched elsewhere;
 * this helper is used only at CB2 authoring/projection boundaries.
 */
export function paletteColorValueToParamRef(value: unknown): ParamRef | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const color = value as Record<string, unknown>;
  if (color['type'] !== 'palette' || typeof color['value'] !== 'string') {
    return null;
  }

  const token = color['value'].trim();
  if (!token) {
    return null;
  }

  return paramRef(
    token.startsWith(`${DEFAULT_COLOR_PALETTE_PARAM_KEY}.`)
      ? token
      : `${DEFAULT_COLOR_PALETTE_PARAM_KEY}.${token}`,
  );
}

/* -------------------------------------------------------------------------- */
/* PropertyValue descriptor accepted by `propertyValue`                       */
/* -------------------------------------------------------------------------- */

/**
 * Descriptor accepted by `propertyValue`. The overloads split on whether a
 * static `value` or an explicit conditional array is provided. The legacy JS
 * resolver (`value: (ctx) => ...`) has been removed: conditions are now
 * declarative (`BlockCondition`) so they serialize to `$cases` in the CB2 spec.
 */
export type PropertyValueDescriptor<
  Type extends DoodFieldType,
  Value = FieldValueByType<Type>,
> = {
  type: Type;
  description?: string;
  options?: Record<string, unknown>;
  /** Dashboard roles allowed to see the field, e.g. `['ADMIN']`. */
  allowedRoles?: readonly string[];
  dashboardVisibilityWhen?: PropertyValueDashboardVisibilityPredicate;
} & (
  | { value: Value | ParamRef | CasesValue; conditions?: never }
  | {
      value?: never;
      conditions: readonly ConditionalPropertyValue<Value | ParamRef | CasesValue>[];
    }
);

/* -------------------------------------------------------------------------- */
/* CB2 property-type projection (re-exported for the projection layer)        */
/* -------------------------------------------------------------------------- */

export type { Cb2PropertyTypeFor };

/**
 * Re-exported so callers can build serializable `$cases` payloads inline when
 * authoring defaults that are not expressible via `BlockCondition` alone.
 */
export type { SerializableValue, CasesValue, ParamRef };
